import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import injectSheet, { WithStylesProps } from 'react-jss';

import { EM_DASH } from '~/common/constants/unicode';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Button } from '~/eds/src/components/Button/Button';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { DataFieldType } from '~/enums/DataFieldType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import * as toastTypes from '~/types/toast.types';
import { getUniqueFieldValues } from '~/utils/document.utils';
import { downloadFile } from '~/utils/files';

import { evisortBlue } from '../../../../assets/shared-styles/general';
import EditIcon from '../../../Shared/Icons/EditIcon';
import SearchIcon from '../../../Shared/Icons/SearchIcon';
import { AttachmentField } from './FieldComponents/AttachmentField';
import { BooleanField } from './FieldComponents/BooleanField';
import { DateField } from './FieldComponents/DateField';
import { MultipleArrayField } from './FieldComponents/MultipleArrayField';
import { NumberField } from './FieldComponents/NumberField';
import { SingleArrayField } from './FieldComponents/SingleArrayField';
import { StringField } from './FieldComponents/StringField';
import { TextAreaField } from './FieldComponents/TextAreaField';
import styles from './InformationField.styles';
import { preprocessFieldValue } from './InformationField.utils';

export interface Field {
  id: number;
  FieldId: string;
  name: string;
  type: DataFieldType;
  value: any;
  values: any[] | null;
  is_editable: boolean;
  is_nullable: boolean;
  allow_new_options: boolean;
  is_flagged: boolean;
  is_beta: boolean;
  class_name: string;
  help_text: string;
  coordinates: any[];
  html_tokens: number[][];
  source: 'wss' | null;
}

interface InformationField2Props extends WithStylesProps<typeof styles> {
  field: Field;
  documentId: string;
  refreshField: () => Promise<void>;
  onFieldTokenClick?: (field: Field) => void;
  userVisibilityLevel: string;
  isTerminated?: boolean;
  isPdfHighlighter?: boolean;
  showOCR?: boolean;
}

const InformationField2Component = ({
  classes,
  field,
  documentId,
  refreshField,
  userVisibilityLevel,
  isTerminated = false,
  isPdfHighlighter,
  showOCR,
  onFieldTokenClick,
}: InformationField2Props) => {
  const intl = useIntl();
  const [editMode, setEditMode] = useState(false);
  const [editingValue, setEditingValue] = useState<any>(null);

  const derivedFieldValue = useMemo(() => {
    if (
      field.type === DataFieldType.ARRAY_MULTIPLE &&
      Array.isArray(field.value)
    ) {
      return getUniqueFieldValues(field.value);
    }
    return field.value;
  }, [field.value, field.type]);

  const fieldValue = editingValue !== null ? editingValue : derivedFieldValue;

  const [updateDocument, { isLoading: isUpdating }] =
    api.endpoints.updateDocument.useMutation();
  const [uploadAttachments] = api.endpoints.uploadAttachments.useMutation();
  const [downloadAttachment] = api.endpoints.downloadAttachment.useLazyQuery();
  const [downloadDocumentAttachment] =
    api.endpoints.downloadDocumentAttachment.useLazyQuery();

  const isWss =
    useFlag(FlagType.WSSDesignatedWorkflow) && field.source === 'wss';
  const disableSelectAllFlag = useFlag(
    FlagType.FieldValueMigrationDeferredFeaturesBypass,
  );
  const DownloadDocumentAttachmentFlag = useFlag(
    FlagType.DownloadDocumentAttachment,
  );
  const canEdit = userVisibilityLevel === 'OPEN' && !isTerminated;

  const handleOnEditClick = useCallback(() => {
    if (!canEdit) return;
    if (!editMode) {
      setEditingValue(derivedFieldValue);
    }
    setEditMode((m) => !m);
  }, [canEdit, editMode, derivedFieldValue]);

  const handleOnCancelClick = useCallback(() => {
    setEditMode(false);
    setEditingValue(null);
  }, []);

  const handleOnSaveClick = useCallback(async () => {
    if (!canEdit) {
      setEditMode(false);
      return;
    }

    const processedFieldValue = preprocessFieldValue(fieldValue);
    const editedFieldData = [
      { id: field.id, label: field.name, value: processedFieldValue },
    ];

    setEditMode(false);

    try {
      await updateDocument({
        documentId: Number(documentId),
        fields: editedFieldData,
      }).unwrap();

      await refreshField();

      setEditingValue(null);

      showToast(
        toastTypes.SUCCESS,
        intl.formatMessage({
          id: 'InformationField.updateSuccessMessage',
          defaultMessage: 'Document information field has been updated.',
          description: 'Success message when field is updated',
        }),
      );
    } catch (e: any) {
      setEditingValue(null);

      if (e.status === 400) {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'InformationField.fieldLimitErrorMessage',
            defaultMessage:
              'Only 30 field values can be added or removed at a time. Please update and try again.',
            description: 'Error message when field value limit is exceeded',
          }),
        );
      } else {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'InformationField.updateFailureMessage',
            defaultMessage:
              'An error occurred while updating information field.',
            description: 'General error message when field update fails',
          }),
        );
      }
    }
  }, [canEdit, fieldValue, documentId, field.id, field.name, refreshField]);

  const downloadFileAttachment = useCallback(
    async (fileObject: any) => {
      try {
        let file: Blob;
        if (DownloadDocumentAttachmentFlag) {
          const response = await downloadDocumentAttachment({
            documentId,
            attachmentId: fileObject.value,
          });
          file = response.data!;
        } else {
          const response = await downloadAttachment({
            attachmentId: fileObject.value,
          });
          file = response.data!;
        }

        showToast(
          toastTypes.DOWNLOAD,
          intl.formatMessage(
            {
              id: 'InformationField.downloadSuccessMessage',
              defaultMessage: '{fileName} successfully downloaded.',
              description:
                'Success message when file is downloaded. Example: fileName = "Document Name"',
            },
            { fileName: fileObject.display_value },
          ),
        );
        downloadFile(file, fileObject.display_value);
      } catch {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'InformationField.downloadFailureMessage',
            defaultMessage: 'Download attachment failed. Please try again.',
            description: 'Error message when file download fails',
          }),
        );
      }
    },
    [documentId],
  );

  const handleFileAttachError = useCallback(() => {
    showToast(
      toastTypes.ERROR,
      intl.formatMessage({
        id: 'InformationField.attachFileFailureMessage',
        defaultMessage: 'Attach file failed. Please try again.',
        description: 'Error message when file attachment fails',
      }),
    );
  }, [intl]);

  const renderFieldValue = (field: Field) => {
    const commonProps = {
      classes,
      field,
      editMode,
      fieldValue,
      setFieldValue: setEditingValue,
    };
    switch (field.type) {
      case DataFieldType.BOOLEAN:
        return <BooleanField {...commonProps} />;
      case DataFieldType.DATE:
        return <DateField {...commonProps} />;
      case DataFieldType.NUMBER:
        return <NumberField {...commonProps} />;
      case DataFieldType.REFERENCE:
      case DataFieldType.STRING:
        return <StringField {...commonProps} />;
      case DataFieldType.ARRAY_SINGLE:
        return <SingleArrayField {...commonProps} />;
      case DataFieldType.ARRAY_MULTIPLE:
        return (
          <MultipleArrayField
            {...commonProps}
            disableSelectAllFlag={disableSelectAllFlag}
          />
        );
      case DataFieldType.TEXT_AREA:
        return <TextAreaField {...commonProps} />;
      case DataFieldType.ATTACHMENT:
        return (
          <AttachmentField
            {...commonProps}
            onUploadAttachments={async (files) =>
              uploadAttachments({ files }).unwrap()
            }
            onDownloadFileAttachment={downloadFileAttachment}
            onHandleFileAttachError={handleFileAttachError}
          />
        );
      default:
        return EM_DASH;
    }
  };

  const sectionFieldClass = () => {
    if (isUpdating && editMode) {
      return classes.sectionFieldEditModeLoading;
    } else if (isUpdating) {
      return classes.sectionFieldLoading;
    }

    return editMode ? classes.sectionFieldEditMode : classes.sectionField;
  };

  const shouldHaveFindButton = () => {
    if (!showOCR) return false;

    return isPdfHighlighter
      ? field.coordinates.length > 0
      : Array.isArray(field.html_tokens) && field.html_tokens.length > 0;
  };

  const renderLoadingSpinner = () => {
    return (
      <div className={classes.loadingContainer}>
        <LoadingSpinner label={`documents spin button ${field.name}`} />
      </div>
    );
  };

  const renderFieldFooter = () => {
    if (!canEdit) return null;

    if (field.is_flagged && !editMode) {
      return (
        <div className={classes.fieldFooter}>
          <Button
            icon="check"
            iconPosition="left"
            text={intl.formatMessage({
              id: 'InformationField.acceptButton',
              defaultMessage: 'Accept',
              description: 'Button text to accept field changes',
            })}
            onClick={handleOnSaveClick}
          />
          <Button
            icon="edit"
            iconPosition="left"
            text={intl.formatMessage({
              id: 'InformationField.editButton',
              defaultMessage: 'Edit',
              description: 'Button text to edit field',
            })}
            onClick={handleOnEditClick}
          />
        </div>
      );
    } else if (editMode) {
      return (
        <div className={classes.fieldFooter}>
          <Button
            icon="check"
            iconPosition="left"
            text={intl.formatMessage({
              id: 'InformationField.saveButton',
              defaultMessage: 'Save',
              description: 'Button text to save field changes',
            })}
            onClick={handleOnSaveClick}
          />
          <Button
            text={intl.formatMessage({
              id: 'InformationField.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Button text to cancel field changes',
            })}
            onClick={handleOnCancelClick}
          />
        </div>
      );
    }

    return null;
  };

  const renderFieldActions = () => {
    const shouldShowEdit = !field.is_flagged && canEdit && !isWss;

    if (editMode || !shouldShowEdit) return null;
    return (
      <div
        tabIndex={-1}
        id={`document_contentsPanel_editKeyInformation?document=${documentId}&keyInformation=${field.id}`}
        className={classes.fieldActionIcons}
      >
        <span className={classes.horizontalSeparator} />
        <button
          onClick={handleOnEditClick}
          title={intl.formatMessage(
            {
              id: 'InformationField.editFieldTooltip',
              defaultMessage: 'Edit {fieldName} field',
              description:
                'Tooltip for edit field button. Example: fieldName = "Document Name"',
            },
            { fieldName: field.name },
          )}
        >
          <EditIcon />
        </button>
      </div>
    );
  };

  return (
    <div className={classes.sectionFieldWrapper} role="menuitem" tabIndex={0}>
      <div className={sectionFieldClass()}>
        <div className={classes.fieldTitle}>
          {field.name}
          {field.is_beta ? (
            <Tooltip
              placement="left"
              tooltip={intl.formatMessage({
                id: 'InformationField.betaTooltip',
                defaultMessage:
                  "This is a newly released intelligent field which is currently in beta testing. If you have feedback about the outputs you're seeing, please send us a note at beta@evisort.com",
                description: 'Tooltip text for beta field indicator',
              })}
            >
              <span className={classes.betaTooltip} data-tip data-for="betaTag">
                {intl.formatMessage({
                  id: 'InformationField.betaLabel',
                  defaultMessage: 'BETA',
                  description: 'Beta field label',
                })}
              </span>
            </Tooltip>
          ) : null}
          {field.help_text ? (
            <Icon
              size="s"
              icon="info"
              id={`help_textTag-${field.id}`}
              tooltip={field.help_text}
              placement="left"
            />
          ) : null}
          {field.source === 'wss' ? (
            <Icon
              icon="database"
              tooltip={intl.formatMessage({
                id: 'InformationField.wssSourceTooltip',
                defaultMessage: 'Source: Strategic Sourcing',
                description: 'Tooltip for WSS field source indicator',
              })}
            />
          ) : null}
          {shouldHaveFindButton() ? (
            <button
              title={intl.formatMessage(
                {
                  id: 'InformationField.findInDocumentTooltip',
                  defaultMessage:
                    'Find {fieldName} information in the document',
                  description:
                    'Tooltip for find in document button. Example: fieldName = "Document Name"',
                },
                { fieldName: field.name },
              )}
              onClick={() => onFieldTokenClick && onFieldTokenClick(field)}
              className={classes.fieldTokenIndicator}
            >
              <SearchIcon color={evisortBlue} size="18" opacity="1" />
            </button>
          ) : null}
        </div>
        <div className={classes.fieldValue}>{renderFieldValue(field)}</div>
        {renderFieldActions()}
        {renderFieldFooter()}
      </div>
      {isUpdating ? renderLoadingSpinner() : null}
    </div>
  );
};

export const InformationFieldV2 = injectSheet(styles)(
  InformationField2Component,
);
